import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  Stack,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import LoadingGif from "../components/LoadingGif";
import { listSubmittedHotels } from "../services/api";

function PaginationArrowIcon({ direction = 'left', disabled = false }) {
  const color = disabled ? '#B8AD9C' : '#5E4B17';
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 477.175 477.175" fill="none" aria-hidden="true">
      {direction === 'left' ? (
        <path
          d="M145.188,238.575l215.5-215.5c5.3-5.3,5.3-13.8,0-19.1s-13.8-5.3-19.1,0l-225.1,225.1c-5.3,5.3-5.3,13.8,0,19.1l225.1,225c2.6,2.6,6.1,4,9.5,4s6.9-1.3,9.5-4c5.3-5.3,5.3-13.8,0-19.1L145.188,238.575z"
          fill={color}
        />
      ) : (
        <path
          d="M360.731,229.075l-225.1-225.1c-5.3-5.3-13.8-5.3-19.1,0s-5.3,13.8,0,19.1l215.5,215.5l-215.5,215.5c-5.3,5.3-5.3,13.8,0,19.1c2.6,2.6,6.1,4,9.5,4c3.4,0,6.9-1.3,9.5-4l225.1-225.1C365.931,242.875,365.931,234.275,360.731,229.075z"
          fill={color}
        />
      )}
    </svg>
  );
}

const AdminListHotels = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:950px)");
  const [filterMode, setFilterMode] = useState('all');
  const [allHotels, setAllHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);

  const getStatusParam = () => {
    if (filterMode === 'all') return null;
    return filterMode;
  };

  const fetchOrdersPage = async (page) => {
    setLoading(true);
    try {
      const status = getStatusParam();
      const response = await listSubmittedHotels(page, 20, status || undefined);
      const hotels = response.data?.data || [];
      setAllHotels(hotels);
      setCurrentPage(response.data?.pagination?.page || page);
      setMaxPage(response.data?.pagination?.totalPages || 1);
      applySearch(hotels, searchText);
    } catch (error) {
      console.error("Failed to fetch hotels:", error);
    } finally {
      setLoading(false);
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > maxPage || page === currentPage) return;
    fetchOrdersPage(page);
  };

  const applySearch = (hotels, query) => {
    if (!query.trim()) {
      setFilteredHotels(hotels);
    } else {
      const filtered = hotels.filter((hotel) =>
        hotel.hotelName?.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredHotels(filtered);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchOrdersPage(1);
  }, [filterMode]);

  useEffect(() => {
    applySearch(allHotels, searchText);
  }, [searchText, allHotels]);

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  const handleEdit = (hotel) => {
    navigate(`/admin-review/${hotel.hotelId}`);
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        <Box
          sx={{
            width: "100%",
          }}
        >
          <Box sx={{ my: isMobile ? 2 : 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              {t("AdminListHotelsTitle")}
            </Typography>

            {/* Filter Tabs */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button
                variant={filterMode === 'all' ? "contained" : "outlined"}
                onClick={() => setFilterMode('all')}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontSize: 16,
                  fontWeight: 600,
                  backgroundColor: filterMode === 'all' ? "#000" : "transparent",
                  color: filterMode === 'all' ? "#fff" : "#000",
                  border: filterMode === 'all' ? "2px solid #000" : "2px solid #999",
                  '&:hover': {
                    backgroundColor: filterMode === 'all' ? "#1a1a1a" : "#f5f5f5",
                  }
                }}
              >
                {t("All")}
              </Button>
              <Button
                variant={filterMode === 'approved' ? "contained" : "outlined"}
                onClick={() => setFilterMode('approved')}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontSize: 16,
                  fontWeight: 600,
                  backgroundColor: filterMode === 'approved' ? "#000" : "transparent",
                  color: filterMode === 'approved' ? "#fff" : "#000",
                  border: filterMode === 'approved' ? "2px solid #000" : "2px solid #999",
                  '&:hover': {
                    backgroundColor: filterMode === 'approved' ? "#1a1a1a" : "#f5f5f5",
                  }
                }}
              >
                {t("Approved")}
              </Button>
              <Button
                variant={filterMode === 'rejected' ? "contained" : "outlined"}
                onClick={() => setFilterMode('rejected')}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontSize: 16,
                  fontWeight: 600,
                  backgroundColor: filterMode === 'rejected' ? "#000" : "transparent",
                  color: filterMode === 'rejected' ? "#fff" : "#000",
                  border: filterMode === 'rejected' ? "2px solid #000" : "2px solid #999",
                  '&:hover': {
                    backgroundColor: filterMode === 'rejected' ? "#1a1a1a" : "#f5f5f5",
                  }
                }}
              >
                {t("Rejected")}
              </Button>
            </Stack>

            {/* Search Bar */}
            <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: isMobile ? "column" : "row", alignItems: "center", gap: 2 }}>
              <TextField
                fullWidth
                placeholder={t('HotelNameLabel')}
                value={searchText}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={20} />
                    </InputAdornment>
                  ),
                  sx: {
                    bgcolor: "white",
                    borderRadius: "8px",
                    height: "48px",
                  },
                }}
                sx={{ maxWidth: 600, }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'flex-end', justifyContent: 'center', gap: 1.25, flexWrap: 'wrap', }}>
                <Button
                  disabled={loading || currentPage === 1}
                  onClick={() => goToPage(currentPage - 1)}
                  sx={{ minWidth: 0, borderRadius: '5px', border: '1px solid #D8CCBA', color: '#5E4B17', px: 1.5 }}
                >
                  <PaginationArrowIcon direction="left" disabled={loading || currentPage === 1} />
                </Button>
                <Box sx={{ px: 2.25, py: 1.2, borderRadius: '5px', border: '1px solid #D8CCBA', color: '#2F2418', backgroundColor: '#FFF', fontSize: { xs: 16, md: 17 } }}>
                  {`Page ${currentPage} / ${maxPage}`}
                </Box>
                <Button
                  disabled={loading || currentPage >= maxPage}
                  onClick={() => goToPage(currentPage + 1)}
                  sx={{ minWidth: 0, borderRadius: '5px', border: '1px solid #D8CCBA', color: '#5E4B17', px: 1.5 }}
                >
                  <PaginationArrowIcon direction="right" disabled={loading || currentPage >= maxPage} />
                </Button>
              </Box>
            </Box>
          </Box>

          {loading && <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh",
              bgcolor: "#fcf8f3",
            }}
          >
            <LoadingGif />
          </Box>}
          {/* Conditional Rendering: Mobile vs Desktop */}
          {!loading &&
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                mb: 4,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: "#F8F8F8" }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>{t("HotelNameLabel")}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>{t("Status")}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>{t("CurrentStep")}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>{t("Actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHotels.map((hotel) => (
                    <TableRow key={hotel.hotelId} hover sx={{
                      '&:hover': {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      }
                    }}>
                      <TableCell>
                        <Typography fontWeight={500}>{hotel.hotelName}</Typography>
                      </TableCell>
                      <TableCell>{hotel.status || '-'}</TableCell>
                      <TableCell>{hotel.currentStep || '-'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(hotel)}
                            title="Edit"
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M18.5 2.49632C18.8978 2.0985 19.4374 1.875 20 1.875C20.5626 1.875 21.1022 2.0985 21.5 2.49632C21.8978 2.89415 22.1213 3.43371 22.1213 3.99632C22.1213 4.55893 21.8978 5.0985 21.5 5.49632L12 14.9963L8 15.9963L9 11.9963L18.5 2.49632Z" stroke="#FFB60C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredHotels.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {searchText ? t("NoHotelsFound") : t("NoHotelsAvailable")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          }
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default AdminListHotels;
